import type { ZudokuConfig } from "zudoku";
import { createApiIdentityPlugin } from "zudoku/plugins";
import {
  CardGroup,
  Icon,
  AccordionGroup,
  Accordion,
  Note,
  Steps,
  Step,
} from "./src/components";
import { Button, Link } from "zudoku/components";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "zudoku/ui/Card";

const CMC_KEY_STORAGE = "cmc_pro_api_key";

const config: ZudokuConfig = {
  site: {
    showPoweredBy: false,
    title: "CoinMarketCap Pro API",
    logo: {
      src: { light: "/logo-light.svg", dark: "/logo-dark.svg" },
      alt: "CoinMarketCap",
      width: "180px",
    },
    footer: {
      social: [
        { icon: "x", href: "https://x.com/CoinMarketCap" },
        { icon: "github", href: "https://github.com/coinmarketcap-official" },
      ],
      copyright: `© ${new Date().getFullYear()} CoinMarketCap. All rights reserved.`,
    },
  },
  metadata: {
    title: "%s — CoinMarketCap Pro API",
    description:
      "High-performance RESTful JSON endpoints for cryptocurrency market data.",
    favicon: "/favicon.ico",
  },
  theme: {
    light: {
      primary: "#3861FB",
      primaryForeground: "#ffffff",
      secondary: "#EFF2F5",
      secondaryForeground: "#1E2329",
    },
    dark: {
      primary: "#3861FB",
      primaryForeground: "#ffffff",
      secondary: "#222531",
      secondaryForeground: "#F8FAFC",
    },
  },
  docs: {
    files: "/pages/**/*.{md,mdx}",
    defaultOptions: {
      showLastModified: false,
    },
  },
  mdx: {
    components: {
      CardGroup,
      Icon,
      Card,
      CardHeader,
      CardTitle,
      CardDescription,
      CardContent,
      AccordionGroup,
      Accordion,
      Note,
      Steps,
      Step,
    },
  },
  apis: {
    type: "file",
    input: "./apis/openapi.json",
    path: "/api",
    options: {
      expandAllTags: false,
      schemaDownload: { enabled: false },
      showInfoPage: true,
    },
  },
  syntaxHighlighting: {
    languages: ["bash", "python", "javascript", "go", "java", "php", "typescript", "json", "yaml", "xml", "html", "css", "scss", "less", "sass", "stylus", "markdown", "md", "mdx", "html", "css", "scss", "less", "sass", "stylus", "markdown", "md", "mdx", "csharp"],
  },
  plugins: [
    createApiIdentityPlugin({
      getIdentities: async () => [
        {
          id: "cmc-api-key",
          label: "CMC Pro API Key",
          authorizeRequest: (request) => {
            let key =
              typeof localStorage !== "undefined"
                ? localStorage.getItem(CMC_KEY_STORAGE)
                : null;

            if (!key) {
              key = prompt("Enter your CoinMarketCap Pro API Key:") ?? "";
              if (key && typeof localStorage !== "undefined") {
                localStorage.setItem(CMC_KEY_STORAGE, key);
              }
            }

            if (key) {
              request.headers.set("X-CMC_PRO_API_KEY", key);
            }
            return request;
          },
          authorizationFields: {
            headers: ["X-CMC_PRO_API_KEY"],
          },
        },
      ],
    }),
  ],
  search: {
    type: "pagefind",
    maxSubResults: 3,
    // Optional: Configure search result ranking (defaults shown below)
    ranking: {
      termFrequency: 0.8,
      pageLength: 0.6,
      termSimilarity: 1.2,
      termSaturation: 1.2,
    },
  },
  navigation: [
    {
      type: "category",
      label: "Guides",
      icon: "book-open",
      items: [
        {
          type: "category",
          label: "Getting Started",
          icon: "rocket",
          collapsed: false,
          collapsible: false,
          items: [
            { type: "doc", file: "index", label: "Introduction", path: "/" },
            "guides/quick-start",
            "guides/authentication",
          ],
        },
        {
          type: "category",
          label: "Reference",
          icon: "file-text",
          collapsed: false,
          collapsible: false,
          items: [
            "guides/standards-and-conventions",
            "guides/errors-and-rate-limits",
            "guides/best-practices",
          ],
        },
      ],
    },
    {
      type: "link",
      label: "Pro API References",
      to: "/api/endpoint-overview",
      icon: "code",
    },
    {
      type: "category",
      label: "AI Tools",
      icon: "cpu",
      collapsed: false,
      collapsible: false,
      items: [
        {
          type: "category",
          label: "AI Integrations",
          icon: "plug",
          items: [
            "ai-tools/overview",
            "api-reference/x402",
            "api-reference/mcp",
          ],
        },
        {
          type: "category",
          label: "IDE Setup",
          icon: "monitor",
          collapsed: false,
      collapsible: false,
          items: [
            "ai-tools/cursor",
            "ai-tools/claude-code",
            "ai-tools/windsurf",
          ],
        },
        {
          type: "category",
          label: "Skills",
          icon: "graduation-cap",
          collapsed: false,
      collapsible: false,
          items: [
            "ai-tools/skills/overview",
            {
              type: "category",
              label: "MCP Skills",
              items: [
                "ai-tools/skills/cmc-mcp",
                "ai-tools/skills/market-report",
                "ai-tools/skills/crypto-research",
              ],
            },
            {
              type: "category",
              label: "x402 Skills",
              items: ["ai-tools/skills/cmc-x402"],
            },
            {
              type: "category",
              label: "API Skills",
              items: [
                "ai-tools/skills/cmc-api-crypto",
                "ai-tools/skills/cmc-api-dex",
                "ai-tools/skills/cmc-api-exchange",
                "ai-tools/skills/cmc-api-market",
              ],
            },
          ],
        },
      ],
    },
    {
      type: "category",
      label: "Changelog",
      icon: "history",
      items: ["changelog/version-history"],
    },
    {
      type: "category",
      label: "FAQ",
      icon: "circle-help",
      items: ["faq/index"],
    },
  ],
  navigationRules: [
    {
      type: "remove",
      match: "Pro API References/Information",
    },
    {
      type: "remove",
      match: "Pro API References/Schemas",
    },
    {
      type: "insert",
      match: "Pro API References/0",
      position: "before",
      items: [
        {
          type: "doc",
          file: "api-reference/endpoint-overview",
          path: "/api/endpoint-overview",
          label: "Endpoint Overview",
          icon: "map",
        },
      ],
    },
    {
      type: "modify",
      match: "Pro API References/Pro API",
      set: { collapsed: false, collapsible: false },
    },
    {
      type: "modify",
      match: "Pro API References/Dex API",
      set: { collapsed: false, collapsible: false },
    },
    {
      type: "modify",
      match: "Pro API References/Tools",
      set: { collapsed: false, collapsible: false },
    },
    {
      type: "modify",
      match: "Pro API References/Deprecated",
      set: { collapsed: false, collapsible: false },
    },
  ],
  redirects: [],
  slots: {
    "head-navigation-end": () => (
      <div className="flex items-center gap-2">
        <Button variant="secondary" asChild><a href="https://pro.coinmarketcap.com/signup" target="_blank">Pricing</a></Button>
        <Button variant="secondary" asChild><a href="https://status.coinmarketcap.com/" target="_blank">API Status</a></Button>
        <Button variant="default" asChild>
          <a href="https://pro.coinmarketcap.com/signup">Get an API Key</a>
        </Button>
      </div>
    ),
  },
};

export default config;